import React, { useState, useCallback, useRef, useMemo } from 'react';
import type { CapacitySpan } from '@interlucent/organization-protocol';
import styles from './WeeklyGridView.module.css';

export interface WeeklyGridViewProps {
  /**
   * Capacity spans to display on the grid
   */
  spans: CapacitySpan[];

  /**
   * Callback when a span is clicked for editing
   */
  onSpanClick?: (span: CapacitySpan) => void;

  /**
   * Callback when a new time range is selected (click-and-drag)
   */
  onTimeRangeSelect?: (selection: {
    dayOfWeek: number;
    startHour: number;
    endHour: number;
  }) => void;

  /**
   * Show capacity intensity via color
   * @default true
   */
  showCapacityIntensity?: boolean;

  /**
   * Disable interactions
   * @default false
   */
  disabled?: boolean;

  /**
   * Additional CSS class
   */
  className?: string;
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

/**
 * WeeklyGridView Component
 *
 * Visual weekly timeline grid showing scheduled capacity spans.
 * Interactive grid for creating and editing recurring weekly schedules.
 *
 * Features:
 * - 7 rows (days) x 24 columns (hours)
 * - Click-and-drag to select time ranges
 * - Click existing span to edit
 * - Hover tooltip showing capacity details
 * - Color intensity indicates capacity level
 * - Geometric Bauhaus design
 *
 * @example
 * ```tsx
 * <WeeklyGridView
 *   spans={schedule.spans}
 *   onSpanClick={(span) => setEditingSpan(span)}
 *   onTimeRangeSelect={({ dayOfWeek, startHour, endHour }) => {
 *     // Create new span
 *   }}
 * />
 * ```
 */
export const WeeklyGridView: React.FC<WeeklyGridViewProps> = ({
  spans,
  onSpanClick,
  onTimeRangeSelect,
  showCapacityIntensity = true,
  disabled = false,
  className,
}) => {
  const [selecting, setSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{ day: number; hour: number } | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<{ day: number; hour: number } | null>(null);
  const [hoveredSpan, setHoveredSpan] = useState<CapacitySpan | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);

  const gridRef = useRef<HTMLDivElement>(null);

  // Filter only recurring spans with time-based schedules
  const recurringSpans = useMemo(() => {
    return spans.filter(
      (span) =>
        span.type === 'recurring' &&
        span.daysOfWeek &&
        span.startTime &&
        span.endTime
    );
  }, [spans]);

  // Parse time string "HH:mm" to hour number
  const parseTimeToHour = (time: string): number => {
    const [hours] = time.split(':').map(Number);
    return hours;
  };

  // Find spans that cover a specific day/hour cell
  const getSpansForCell = useCallback(
    (day: number, hour: number): CapacitySpan[] => {
      return recurringSpans.filter((span) => {
        if (!span.daysOfWeek?.includes(day)) return false;

        const startHour = parseTimeToHour(span.startTime!);
        const endHour = parseTimeToHour(span.endTime!);

        // Handle overnight spans (e.g., 22:00 - 02:00)
        if (endHour < startHour) {
          return hour >= startHour || hour < endHour;
        }

        return hour >= startHour && hour < endHour;
      });
    },
    [recurringSpans]
  );

  // Calculate capacity intensity (0-1) for color coding
  const getCapacityIntensity = (span: CapacitySpan): number => {
    // Use desired capacity as the intensity indicator
    // Normalize to a reasonable range (0-20 instances)
    const maxExpected = 20;
    const value = span.ceiling ?? span.floor ?? span.expectedSessions ?? 0;
    return Math.min(value / maxExpected, 1);
  };

  const handleCellMouseDown = useCallback(
    (day: number, hour: number) => (e: React.MouseEvent) => {
      if (disabled) return;

      // Check if clicking on an existing span
      const cellSpans = getSpansForCell(day, hour);
      if (cellSpans.length > 0 && onSpanClick) {
        // Click the topmost (last) span
        onSpanClick(cellSpans[cellSpans.length - 1]);
        return;
      }

      // Start new selection
      if (onTimeRangeSelect) {
        e.preventDefault();
        setSelecting(true);
        setSelectionStart({ day, hour });
        setSelectionEnd({ day, hour });
      }
    },
    [disabled, getSpansForCell, onSpanClick, onTimeRangeSelect]
  );

  const handleCellMouseEnter = useCallback(
    (day: number, hour: number) => {
      if (selecting && selectionStart) {
        // Only allow selection on the same day
        if (day === selectionStart.day) {
          setSelectionEnd({ day, hour });
        }
      }
    },
    [selecting, selectionStart]
  );

  const handleCellMouseMove = useCallback(
    (span: CapacitySpan | null) => (e: React.MouseEvent) => {
      if (span) {
        setHoveredSpan(span);
        setTooltipPosition({ x: e.clientX, y: e.clientY });
      } else {
        setHoveredSpan(null);
        setTooltipPosition(null);
      }
    },
    []
  );

  const handleMouseUp = useCallback(() => {
    if (selecting && selectionStart && selectionEnd && onTimeRangeSelect) {
      const dayOfWeek = selectionStart.day;
      const startHour = Math.min(selectionStart.hour, selectionEnd.hour);
      const endHour = Math.max(selectionStart.hour, selectionEnd.hour) + 1; // End is exclusive

      onTimeRangeSelect({ dayOfWeek, startHour, endHour });
    }

    setSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
  }, [selecting, selectionStart, selectionEnd, onTimeRangeSelect]);

  // Attach global mouse up listener
  React.useEffect(() => {
    if (selecting) {
      document.addEventListener('mouseup', handleMouseUp);
      return () => document.removeEventListener('mouseup', handleMouseUp);
    }
  }, [selecting, handleMouseUp]);

  // Check if cell is in current selection
  const isCellInSelection = useCallback(
    (day: number, hour: number): boolean => {
      if (!selecting || !selectionStart || !selectionEnd) return false;
      if (day !== selectionStart.day) return false;

      const minHour = Math.min(selectionStart.hour, selectionEnd.hour);
      const maxHour = Math.max(selectionStart.hour, selectionEnd.hour);

      return hour >= minHour && hour <= maxHour;
    },
    [selecting, selectionStart, selectionEnd]
  );

  return (
    <div className={`${styles.weeklyGrid} ${className || ''}`} ref={gridRef}>
      {/* Header - Hour labels */}
      <div className={styles.gridHeader}>
        <div className={styles.cornerCell} />
        {HOURS.map((hour) => (
          <div key={hour} className={styles.hourLabel}>
            {String(hour).padStart(2, '0')}
          </div>
        ))}
      </div>

      {/* Grid rows - One per day */}
      {DAYS.map((dayName, dayIndex) => (
        <div key={dayIndex} className={styles.gridRow}>
          {/* Day label */}
          <div className={styles.dayLabel}>{dayName.slice(0, 3).toUpperCase()}</div>

          {/* Hour cells */}
          {HOURS.map((hour) => {
            const cellSpans = getSpansForCell(dayIndex, hour);
            const topSpan = cellSpans[cellSpans.length - 1];
            const isSelected = isCellInSelection(dayIndex, hour);
            const intensity = topSpan && showCapacityIntensity ? getCapacityIntensity(topSpan) : 0;

            return (
              <div
                key={hour}
                className={`${styles.cell} ${topSpan ? styles.cellOccupied : ''} ${isSelected ? styles.cellSelected : ''}`}
                style={
                  topSpan
                    ? {
                        backgroundColor: `rgba(var(--accent-rgb, 196, 81, 61), ${0.2 + intensity * 0.6})`,
                      }
                    : undefined
                }
                onMouseDown={handleCellMouseDown(dayIndex, hour)}
                onMouseEnter={() => handleCellMouseEnter(dayIndex, hour)}
                onMouseMove={handleCellMouseMove(topSpan)}
                role="gridcell"
                aria-label={`${dayName} ${hour}:00`}
              />
            );
          })}
        </div>
      ))}

      {/* Tooltip for hovered span */}
      {hoveredSpan && tooltipPosition && (
        <div
          className={styles.tooltip}
          style={{
            left: tooltipPosition.x + 10,
            top: tooltipPosition.y + 10,
          }}
        >
          <div className={styles.tooltipTitle}>
            {hoveredSpan.description || 'Capacity Span'}
          </div>
          <div className={styles.tooltipDetail}>
            <strong>Time:</strong> {hoveredSpan.startTime} - {hoveredSpan.endTime}
          </div>
          <div className={styles.tooltipDetail}>
            <strong>Days:</strong>{' '}
            {hoveredSpan.daysOfWeek?.map((d) => DAYS[d].slice(0, 3)).join(', ')}
          </div>
          {hoveredSpan.floor !== undefined && (
            <div className={styles.tooltipDetail}>
              <strong>Floor:</strong> {hoveredSpan.floor}
            </div>
          )}
          {hoveredSpan.ceiling !== undefined && (
            <div className={styles.tooltipDetail}>
              <strong>Ceiling:</strong> {hoveredSpan.ceiling}
            </div>
          )}
          {hoveredSpan.floor === undefined && hoveredSpan.ceiling === undefined && hoveredSpan.expectedSessions !== undefined && (
            <div className={styles.tooltipDetail}>
              <strong>Sessions:</strong> {hoveredSpan.expectedSessions}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
